/**
 * SQL-режим конструктора отчётов: превращает плоские строки ответа
 * `report/builder/get-data-sql` в каноническую структуру chartData, которую
 * уже умеет рисовать `components/reports/_chart.vue`.
 *
 * Маппинг колонок живёт в том же source_request, что и у остальных отчётов:
 *   - source_request.x           — колонка оси X / категории (в трендах это всегда дата)
 *   - source_request.requests[]  — { type: 'sql_column', column, label, color } = серия/метрика
 *   - source_request.separators  — колонка-разбивка (как в трендах)
 *   - source_request.breakdown_limit — сколько разбивок показывать (top-N)
 *
 * Модуль намеренно чистый (без стора и i18n) — его гоняет мутация стора и,
 * в перспективе, изолированное состояние дашборд-виджета.
 */

// Типы графиков, которые схлопывают ряд в одно число на серию (см. buildAggregatedData в _chart.vue)
const AGGREGATED_TYPES = ['pie', 'bar_horizontal', 'number', 'map']

const DATE_RE = /^\d{4}-\d{2}-\d{2}([ T]\d{2}:\d{2}(:\d{2})?)?$/
const NUMBER_RE = /^-?\d+(\.\d+)?$/

// ClickHouse отдаёт числа строками — иначе метрики выглядели бы как категории
const isNumeric = (v) => typeof v === 'number' ? Number.isFinite(v) : (typeof v === 'string' && NUMBER_RE.test(v.trim()))
const toNumber = (v) => {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

const toLabel = (v) => {
  if (v === null || v === undefined || v === '') return '—'
  if (typeof v === 'object') return JSON.stringify(v)
  return String(v)
}

/**
 * Значение категории в том же виде, в каком оно попадает в `separators` chartData.
 * Нужен таблице результата, чтобы её ключи оформления совпадали с ключами графика.
 */
export const formatCategoryValue = toLabel

/**
 * Значения разбивки для строки результата — ровно те, что окажутся в `separators`
 * соответствующего элемента chartData. `null`, если у графика категорий нет
 * (например, временной ряд без разбивки).
 */
export function rowCategory(row, sourceRequest, typeChart) {
  const x = sourceRequest?.x || null
  const separator = (sourceRequest?.separators || [])[0] || null

  if (AGGREGATED_TYPES.includes(typeChart)) {
    const category = x || separator
    if (!category) return null
    const extra = x ? separator : null
    return extra ? [toLabel(row?.[category]), toLabel(row?.[extra])] : [toLabel(row?.[category])]
  }

  return separator ? [toLabel(row?.[separator])] : null
}

/**
 * Определяет тип каждой колонки по значениям (первые 50 непустых) и число
 * различных значений — по нему видно ключ группировки среди числовых колонок.
 * @returns {Array<{name: string, type: 'date'|'number'|'string', distinct: number}>}
 */
export function detectColumns(rows) {
  if (!Array.isArray(rows) || !rows.length || typeof rows[0] !== 'object' || rows[0] === null) return []

  return Object.keys(rows[0]).map(name => {
    let seen = 0
    let dates = 0
    let numbers = 0
    const values = new Set()
    for (const row of rows) {
      const value = row?.[name]
      values.add(toLabel(value))
      if (value === null || value === undefined || value === '') continue
      if (seen < 50) {
        seen++
        if (typeof value === 'string' && DATE_RE.test(value.trim())) dates++
        else if (isNumeric(value)) numbers++
      }
    }
    const distinct = values.size
    if (!seen) return { name, type: 'string', distinct }
    if (dates === seen) return { name, type: 'date', distinct }
    if (numbers === seen) return { name, type: 'number', distinct }
    return { name, type: 'string', distinct }
  })
}

/**
 * Подбирает визуализацию по форме результата. Вызывается после выполнения запроса,
 * если маппинг ещё не задан или ссылается на исчезнувшие колонки.
 *
 * @returns {{type_chart: string, x: string|null, metrics: string[], separators: string[]}}
 */
export function autodetectMapping(columns, rowCount = 0) {
  const dates = columns.filter(c => c.type === 'date').map(c => c.name)
  const numbers = columns.filter(c => c.type === 'number').map(c => c.name)
  const strings = columns.filter(c => c.type === 'string').map(c => c.name)

  const fallback = { type_chart: 'table', x: null, metrics: [], separators: [] }
  if (!columns.length) return fallback

  // Явные подсказки алиасами: SELECT ... AS x / AS series
  const hintedX = columns.find(c => c.name === 'x')?.name || null
  const hintedSeries = columns.find(c => c.name === 'series')?.name || null

  // Нечего рисовать — без числовой колонки остаётся только таблица
  if (!numbers.length) return fallback

  // Временной ряд: есть дата → линия
  const x = hintedX || dates[0] || null
  if (x && (hintedX || dates.length)) {
    const rest = strings.filter(name => name !== x)
    const separators = hintedSeries ? [hintedSeries] : (rest.length && numbers.length === 1 ? [rest[0]] : [])
    const metrics = numbers.filter(name => !separators.includes(name))
    return { type_chart: 'line', x, metrics, separators }
  }

  // Категории: одна текстовая колонка + метрики
  if (strings.length) {
    const category = strings[0]
    const rest = strings.filter(name => name !== category)
    const separators = hintedSeries ? [hintedSeries] : (rest.length && numbers.length === 1 ? [rest[0]] : [])
    const metrics = numbers.filter(name => !separators.includes(name))
    const compact = rowCount > 0 && rowCount <= 12 && metrics.length === 1 && !separators.length
    return { type_chart: compact ? 'pie' : 'bar_horizontal', x: category, metrics, separators }
  }

  // Только числа: одна строка — это KPI
  if (rowCount === 1) return { type_chart: 'number', x: null, metrics: numbers, separators: [] }

  // Числовая категория: `SELECT users.gender, count(*) ... GROUP BY users.gender` —
  // ключ группировки тоже число, но уникален в каждой строке, в отличие от метрики.
  if (numbers.length >= 2) {
    const first = columns.find(c => c.name === numbers[0])
    if (first && first.distinct === rowCount) {
      const metrics = numbers.slice(1)
      const compact = rowCount <= 12 && metrics.length === 1
      return { type_chart: compact ? 'pie' : 'bar_horizontal', x: numbers[0], metrics, separators: [] }
    }
  }

  return fallback
}

/**
 * Считает, что маппинг всё ещё применим к пришедшим колонкам.
 */
export function isMappingUsable(sourceRequest, columns) {
  const names = columns.map(c => c.name)
  const metrics = (sourceRequest?.requests || []).filter(r => r?.column)
  if (!metrics.length) return false
  if (!metrics.every(r => names.includes(r.column))) return false
  if (sourceRequest.x && !names.includes(sourceRequest.x)) return false
  return (sourceRequest.separators || []).every(name => names.includes(name))
}

/**
 * Строки SQL → chartData в формате, который ждёт `_chart.vue`:
 *   ряды:        [{ date: [...ось X], total: [...значения], separators: [], request_index }]
 *   агрегаты:    [{ total: [сумма], separators: [категория], request_index }]
 *
 * @param {Array<Object>} rows       строки ответа get-data-sql
 * @param {Object} sourceRequest     source_request SQL-отчёта
 * @param {string} typeChart         тип графика
 * @param {{otherLabel?: string}} options
 */
export function buildSqlChartData(rows, sourceRequest, typeChart, options = {}) {
  const requests = (sourceRequest?.requests || []).filter(r => r?.column)
  if (!Array.isArray(rows) || !rows.length || !requests.length) return []

  const x = sourceRequest.x || null
  const separator = (sourceRequest.separators || [])[0] || null
  const limit = Number(sourceRequest.breakdown_limit) > 0 ? Number(sourceRequest.breakdown_limit) : 25

  return AGGREGATED_TYPES.includes(typeChart)
    ? buildAggregated(rows, requests, x, separator, limit, options.otherLabel || 'Other')
    : buildSeries(rows, requests, x, separator, limit)
}

/**
 * Линии/столбцы: ось X + одна серия на (метрику × значение разбивки).
 * Порядок точек по X — порядок строк ответа, чтобы ORDER BY пользователя не переигрывался.
 */
function buildSeries(rows, requests, x, separator, limit) {
  const axis = []
  const axisIndex = new Map()
  for (const row of rows) {
    const key = x ? toLabel(row?.[x]) : ''
    if (!axisIndex.has(key)) {
      axisIndex.set(key, axis.length)
      axis.push(key)
    }
  }

  // groupKey → { requestIndex, separatorValue, values[] }
  const groups = new Map()
  for (const row of rows) {
    const position = axisIndex.get(x ? toLabel(row?.[x]) : '')
    const separatorValue = separator ? toLabel(row?.[separator]) : null

    requests.forEach((request, requestIndex) => {
      const groupKey = requestIndex + ' ' + (separatorValue ?? '')
      if (!groups.has(groupKey)) {
        groups.set(groupKey, {
          requestIndex,
          separatorValue,
          values: new Array(axis.length).fill(0),
          sum: 0
        })
      }
      const group = groups.get(groupKey)
      const value = toNumber(row?.[request.column])
      group.values[position] += value
      group.sum += value
    })
  }

  let items = Array.from(groups.values())
  // Разбивка может дать сотни серий — оставляем самые крупные
  if (separator && items.length > limit * requests.length) {
    items = items.sort((a, b) => b.sum - a.sum).slice(0, limit * requests.length)
  }

  return items.map(group => ({
    date: axis,
    total: group.values,
    separators: group.separatorValue === null ? [] : [group.separatorValue],
    request_index: group.requestIndex
  }))
}

/**
 * Круговая/горизонтальные столбцы/KPI: одно число на (категорию × метрику),
 * хвост категорий сворачивается в «Прочее».
 */
function buildAggregated(rows, requests, x, separator, limit, otherLabel) {
  const category = x || separator
  const extra = x ? separator : null

  if (!category) {
    // Ни категории, ни разбивки: каждая метрика — отдельный сектор/плитка
    return requests.map((request, requestIndex) => ({
      total: [rows.reduce((sum, row) => sum + toNumber(row?.[request.column]), 0)],
      separators: [],
      request_index: requestIndex
    }))
  }

  const buckets = new Map()
  for (const row of rows) {
    const categoryValue = toLabel(row?.[category])
    const extraValue = extra ? toLabel(row?.[extra]) : null
    const bucketKey = categoryValue + ' ' + (extraValue ?? '')
    if (!buckets.has(bucketKey)) {
      buckets.set(bucketKey, {
        labels: extraValue === null ? [categoryValue] : [categoryValue, extraValue],
        sums: new Array(requests.length).fill(0)
      })
    }
    const bucket = buckets.get(bucketKey)
    requests.forEach((request, requestIndex) => {
      bucket.sums[requestIndex] += toNumber(row?.[request.column])
    })
  }

  let list = Array.from(buckets.values()).sort((a, b) => b.sums[0] - a.sums[0])
  if (list.length > limit) {
    const tail = list.slice(limit - 1)
    list = list.slice(0, limit - 1)
    const rest = { labels: [otherLabel], sums: new Array(requests.length).fill(0) }
    for (const bucket of tail) {
      bucket.sums.forEach((value, i) => { rest.sums[i] += value })
    }
    list.push(rest)
  }

  const items = []
  requests.forEach((request, requestIndex) => {
    for (const bucket of list) {
      items.push({
        total: [bucket.sums[requestIndex]],
        separators: bucket.labels,
        request_index: requestIndex
      })
    }
  })
  return items
}

/**
 * Раскладывает ответ get-data-sql по состоянию отчёта: строки, колонки, маппинг
 * (подбирается автоматически, если старый больше не применим) и chartData.
 *
 * Состояние — либо стор конструктора, либо локальное состояние виджета/страницы
 * просмотра: поля у них одинаковые, поэтому логика живёт здесь, а не в мутации.
 *
 * @param {Object} state объект с полями source_request / type_chart / sqlRows / chartData
 * @param {*} response   ответ эндпоинта ({data, total_count} или массив строк)
 */
export function applySqlResult(state, response, options = {}) {
  let rows = []
  if (response && typeof response === 'object' && !Array.isArray(response) && response.data !== undefined) {
    rows = Array.isArray(response.data) ? response.data : []
    state.total_count = response.total_count || rows.length
  } else {
    rows = Array.isArray(response) ? response : (response ? [response] : [])
    state.total_count = rows.length
  }

  if (!state.source_request) state.source_request = {}

  state.sqlErrors = []
  state.sqlRows = rows
  state.sqlColumns = detectColumns(rows)

  if (!isMappingUsable(state.source_request, state.sqlColumns)) {
    const guess = autodetectMapping(state.sqlColumns, rows.length)
    state.source_request.x = guess.x
    state.source_request.separators = guess.separators
    state.source_request.requests = guess.metrics.map(column => ({
      type: 'sql_column',
      column,
      label: column,
      color: null
    }))
    state.type_chart = guess.type_chart
    state.source_request.type_chart = guess.type_chart
    state.hiddenDatasets = []
  }

  state.chartData = buildSqlChartData(rows, state.source_request, state.type_chart, options)
  return state
}

export default {
  detectColumns,
  autodetectMapping,
  isMappingUsable,
  buildSqlChartData,
  applySqlResult,
  rowCategory,
  formatCategoryValue
}
